import {inject, Injectable, signal} from '@angular/core';
import {firstValueFrom, forkJoin, Observable, of, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
  ChatLibraryApiItem, ChatLibraryApiResponse, ChatLibraryCreative, CreativesMetadataResponse,
  ChatLibraryState,
  LibraryGroup, CreativeCardProps,
} from '../models/chat-library.models';
import {CommunicationService} from '../../../../http/communication.service';


export type ChatLibraryData = { id: string, chatLibrary: ChatLibraryApiResponse | undefined, creativeUrls?: string[] };

const CHAT_LIBRARY_API = '/chat-library';
const CREATIVES_API = `${CHAT_LIBRARY_API}/creatives/metadata`;
export const CREATIVES_PAGE_SIZE = 12;
const INSIGHTS_URL_PREFIX = 'https://insights.browsi.com';
const SOCIAL_CREATIVE_IDENTIFIER = '/meta-creatives/';
const EMPTY_RESULT_GRACE_PERIOD_MS = 4000;

const INITIAL_STATE: ChatLibraryState = {
  isOpen: false,
  isLoading: false,
  isLoadingMore: false,
  hasContent: false,
  activeGroupId: null,
  groups: [],
};

@Injectable({providedIn: 'root'})
export class ChatLibraryService {
  private readonly communicationService = inject(CommunicationService);

  private readonly _state = signal<ChatLibraryState>(INITIAL_STATE);
  readonly state = this._state.asReadonly();

  private readonly _hasTimedOutEmpty = signal(false);
  readonly hasTimedOutEmpty = this._hasTimedOutEmpty.asReadonly();
  private gracePeriodTimer: ReturnType<typeof setTimeout> | undefined;

  private readonly _scrollToGroup$ = new Subject<string>();
  readonly scrollToGroup$ = this._scrollToGroup$.asObservable();

  private readonly _scrollToPassage$ = new Subject<{ passageId: string; start: number; end: number }>();
  readonly scrollToPassage$ = this._scrollToPassage$.asObservable();

  private loadEpoch = 0;

  resetLibrary(): void {
    this.loadEpoch++;
    this._state.set(INITIAL_STATE);
    this.clearGracePeriod();
  }

  startGracePeriod(): void {
    if (this._state().groups.length > 0) {
      return;
    }
    this._hasTimedOutEmpty.set(false);
    if (this.gracePeriodTimer) {
      clearTimeout(this.gracePeriodTimer);
    }
    const epoch = this.loadEpoch;
    this.gracePeriodTimer = setTimeout(() => {
      this.gracePeriodTimer = undefined;
      if (epoch === this.loadEpoch && this._state().groups.length === 0) {
        this._hasTimedOutEmpty.set(true);
      }
    }, EMPTY_RESULT_GRACE_PERIOD_MS);
  }

  private clearGracePeriod(): void {
    if (this.gracePeriodTimer) {
      clearTimeout(this.gracePeriodTimer);
      this.gracePeriodTimer = undefined;
    }
    this._hasTimedOutEmpty.set(false);
  }

  setActiveGroup(groupId: string | null): void {
    this._state.update(current => ({...current, activeGroupId: groupId}));
  }

  openPanel(): void {
    this._state.update(current => ({...current, isOpen: true}));
  }

  closePanel(): void {
    this._state.update(current => ({...current, isOpen: false}));
  }

  scrollToGroup(groupId: string): void {
    this._scrollToGroup$.next(groupId);
  }

  focusOnGroup(group: LibraryGroup): void {
    const { answerPassageId, textLocation } = group;
    this.setActiveGroup(group.id);
    this.scrollToPassage(answerPassageId, textLocation.start, textLocation.end);
  }

  scrollToPassage(passageId: string, start: number, end: number): void {
    this._scrollToPassage$.next({passageId, start, end});
  }

  async loadCreativeLibrary(messages: ChatLibraryData[], append = false): Promise<number> {
    const allItemIds = messages.flatMap(m => this.getItemIds(m));
    if (allItemIds.length === 0) {
      return 0;
    }
    const epoch = this.loadEpoch;
    const loadingKey = append ? 'isLoadingMore' : 'isLoading';
    this._state.update(current => ({...current, [loadingKey]: true}));
    try {
      const creativesMetadata = await firstValueFrom(this.fetchCreativesData(allItemIds));
      if (epoch !== this.loadEpoch) {
        return 0;
      }
      if (creativesMetadata.creatives.length > 0) {
        this.setGroupsFromPolarisResponse(messages, creativesMetadata, append);
      }
      return creativesMetadata.creatives.length;
    } finally {
      if (epoch === this.loadEpoch) {
        this._state.update(current => ({...current, [loadingKey]: false}));
      }
    }
  }

  private getItemIds(message: ChatLibraryData): string[] {
    if (message.chatLibrary?.chatLibrary.length) {
      return message.chatLibrary.chatLibrary.map(item => item.itemId);
    }
    return (message.creativeUrls ?? []).map(url => this.stripInsightsUrlPrefix(url));
  }

  private stripInsightsUrlPrefix(url: string): string {
    return url.startsWith(INSIGHTS_URL_PREFIX) ? url.slice(INSIGHTS_URL_PREFIX.length) : url;
  }

  extractCreativeIdFromUrl(url: string): string {
    if (url.startsWith(INSIGHTS_URL_PREFIX + SOCIAL_CREATIVE_IDENTIFIER)) {
      return this.stripInsightsUrlPrefix(url);
    } else if (url.startsWith(SOCIAL_CREATIVE_IDENTIFIER)) {
      return url;
    }
    const fileName = url.split('/').pop() ?? url;
    return fileName.replace(/\.[^.]+$/, '');
  }

  // The server sometimes returns a creative id with a different extension (e.g. .jpg)
  // than the itemId it was requested with (e.g. .mp4), even though it's the same asset.
  private stripExtension(value: string): string {
    return value.replace(/\.[^.]+$/, '');
  }

  private matchesItemId(value: string | undefined, itemId: string): boolean {
    return !!value && this.stripExtension(this.extractCreativeIdFromUrl(value)) === this.stripExtension(itemId);
  }

  setGroupsFromPolarisResponse(messages: ChatLibraryData[], creativesResponse: CreativesMetadataResponse, append = false): void {
    const libraryGroups: LibraryGroup[] = this.buildGroups(messages, creativesResponse.creatives);
    this._state.update(current => {
      const groups = append ? this.mergeGroups(current.groups, libraryGroups) : libraryGroups;
      return {...current, groups, hasContent: groups.length > 0};
    });
    if (this._state().groups.length > 0) {
      this.clearGracePeriod();
    }
  }

  private mergeGroups(existing: LibraryGroup[], incoming: LibraryGroup[]): LibraryGroup[] {
    const merged = [...existing];
    for (const group of incoming) {
      const index = merged.findIndex(g => g.id === group.id);
      if (index >= 0) {
        merged[index] = {...merged[index], cards: [...merged[index].cards, ...group.cards]};
      } else {
        merged.push(group);
      }
    }
    return merged;
  }

  private fetchCreativesData(itemIds: string[]): Observable<CreativesMetadataResponse> {
    const uniqueIds = [...new Set(itemIds)].filter(Boolean);
    const pages: string[][] = [];
    for (let i = 0; i < uniqueIds.length; i += CREATIVES_PAGE_SIZE) {
      pages.push(uniqueIds.slice(i, i + CREATIVES_PAGE_SIZE));
    }
    if (pages.length === 0) {
      return of<CreativesMetadataResponse>({creatives: []});
    }
    return forkJoin(pages.map(pageIds => this.fetchCreativesPage(pageIds))).pipe(
      map(responses => ({creatives: responses.flatMap(r => r.creatives)}))
    );
  }

  private fetchCreativesPage(pageIds: string[]): Observable<CreativesMetadataResponse> {
    return this.communicationService
      .httpPost<CreativesMetadataResponse>(CREATIVES_API, {ids: pageIds})
      .pipe(
        catchError(error => {
          console.error('[ChatLibraryService] fetchCreativesData failed', error);
          return of<CreativesMetadataResponse>({creatives: []});
        })
      );
  }

  private buildGroups(
    messages: ChatLibraryData[],
    creatives: ChatLibraryCreative[]
  ): LibraryGroup[] {
    const groups: LibraryGroup[] = [];

    for (const message of messages) {
      if (!message.chatLibrary?.chatLibrary.length) {
        groups.push(...this.buildGroupFromCreativeUrls(message.creativeUrls ?? [], creatives, message.id));
        continue;
      }

      const grouped = new Map<string, ChatLibraryApiItem[]>();
      for (const item of message.chatLibrary.chatLibrary) {
        const existing = grouped.get(item.itemGroupId) ?? [];
        grouped.set(item.itemGroupId, [...existing, item]);
      }

      for (const [groupId, groupItems] of grouped.entries()) {
        const cards = groupItems
          .map(gi => {
            const creative = creatives.find(c =>
              this.matchesItemId(c.id, gi.itemId) || this.matchesItemId(c.thumbnailUrl, gi.itemId));
            return creative ? {...creative, creativeUrl: creative.id} : undefined;
          })
          .filter((c): c is CreativeCardProps => !!c);

        const groupLabel = message.chatLibrary.groups.find(g => g.groupId === groupId);

        groups.push({
          id: groupId,
          label: groupLabel?.label || `Creatives Group`,
          answerPassageId: message.id,
          textLocation: groupLabel?.location || {start: 0, end: 0},
          cards: cards.slice(0, 8),
        });
      }
    }
    return groups;
  }

  private buildGroupFromCreativeUrls(
    creativeUrls: string[],
    creatives: ChatLibraryCreative[],
    answerId: string
  ): LibraryGroup[] {
    const cards = creativeUrls
      .map(url => {
        const creativeId = this.extractCreativeIdFromUrl(url);
        const creativeMatch = creatives.find(c => c.id === creativeId || c.thumbnailUrl === creativeId);
        return creativeMatch ? {...creativeMatch, creativeUrl: url} : undefined;
      })
      .filter((c): c is CreativeCardProps => !!c);

    if (!cards.length) {
      return [];
    }

    return [{
      id: answerId,
      label: 'Creatives',
      answerPassageId: answerId,
      textLocation: {start: 0, end: 0},
      cards: cards,
    }];
  }
}
